<script lang="ts">
import { defineAsyncComponent, defineComponent } from 'vue'
import { Keyboard } from '@capacitor/keyboard'
import { isNativeMobileApp } from '@/addons/mobile'
import LoaderController from '@/components/loader/LoaderController'
import FeModalPicking from '@/modules/picking/modal-picking/modal-picking.vue'
import FeCatalogFlingLedWallModal from '@/abs-core/pages/catalog/plp/fling/FlingLedWallPickModal.vue'

const feButton = defineAsyncComponent(
  () => import('@/components/global/button.vue'),
)
const feIconComponent = defineAsyncComponent(
  () => import('@/components/global/icon.vue'),
)
const FeReturnsPayments = defineAsyncComponent(
  () => import('@/components/modal/returns-payments.vue'),
)
const FeToolsAdvanceModalInput = defineAsyncComponent(
  () => import('@/components/modal/tools-advance-modal-input.vue'),
)
const FeTaxfreeError = defineAsyncComponent(
  () => import('@/components/recap/fattura/modal/taxfree-error.vue'),
)
const FeModalCreditCard = defineAsyncComponent(
  () => import('@/components/returns/partials/modal-credit-card.vue'),
)

const FeModalDisableCard = defineAsyncComponent(
  () => import('@/modules/loyalty/components/modals/disable-card.vue'),
)
const FeModalTransferPoints = defineAsyncComponent(
  () => import('@/modules/loyalty/components/modals/transfer-points.vue'),
)

export default defineComponent({
  name: 'FeModal',
  components: {
    feButton,
    'fe-icon': feIconComponent,
    FeModalCreditCard,
    FeReturnsPayments,
    FeTaxfreeError,
    FeToolsAdvanceModalInput,
    FeModalDisableCard,
    FeModalTransferPoints,
    FeModalPicking,
    FeCatalogFlingLedWallModal,
  },
  // eslint-disable-next-line vue/require-prop-types
  props: ['modal'],
  emits: ['returnTransaction', 'handleConfirmClick', 'close'],
  computed: {
    bindProps() {
      return {
        ...this.modal.customProps,
        customEventHandlers: this.modal.customEventHandlers,
        customProps: this.modal.customProps,
        payments: this.modal.customProps?.payments,
      }
    },
  },
  created() {
    if (isNativeMobileApp()) {
      Keyboard.hide()
    }
  },
  methods: {
    close() {
      LoaderController.hide()
      this.$emit('close')
    },
    handleConfirmClick() {
      this.$emit('handleConfirmClick')
    },
  },
})
</script>

<template>
  <Teleport :to="modal.teleportTo" :disabled="!modal.teleportTo">
    <div class="fe-modal">
      <div v-if="modal" class="overlay">
        <div
          class="modal"
          :class="[
            modal.customClass || '',
            modal.type || '',
            { [!modal.customFooter]: '--no-footer' },
          ]"
          :data-test="modal.name"
        >
          <component
            :is="modal.component"
            v-if="modal.type === 'modal-stepper'"
            :messages="modal.taxfreeMessages"
            v-bind="bindProps"
            @close="close"
          />
          <template v-else>
            <header class="header">
              <p class="header__title">{{ modal.title }}</p>

              <fe-button
                v-if="modal.closeIcon === true"
                color="icon"
                class="header__close"
                @click="close"
              >
                <fe-icon class="header__icon" icon="close" />
              </fe-button>
            </header>
            <!-- v-html used here because the label contains html tags -->
            <!-- eslint-disable vue/no-v-html -->
            <div class="body">
              <p
                v-if="modal.message"
                :class="'align-' + modal.textAlign"
                v-html="modal.message"
              />
              <div v-if="modal.hasLoader" class="modalLoader-wrapper">
                <div class="modalLoader-container show">
                  <span class="modalLoader generic"></span>
                </div>
              </div>
              <component
                :is="modal.component"
                :messages="modal.taxfreeMessages"
                v-bind="bindProps"
                @close="close"
              />
            </div>

            <footer
              v-if="!modal.customFooter"
              class="footer"
              :class="'align-' + modal.buttonAlign"
            >
              <fe-button
                v-if="!modal.noCancelButton"
                class="with_border"
                data-test="modal__cancel__button"
                :color="modal.customButtons?.close?.color ?? 'border'"
                :class="`${modal.confirmActionButton ? 'close-btn' : ''} ${
                  modal.borderButtons
                }`"
                :fill="modal.customButtons?.close?.fill ?? 'outline'"
                @click="close"
              >
                <span>{{
                  modal.customButtons?.close?.label
                    ? $t(modal.customButtons?.close?.label)
                    : modal.abortLabel
                      ? modal.abortLabel
                      : $t('pos_common.cancel')
                }}</span>
                <template #icon-right>
                  <fe-icon
                    :icon="modal.customButtons?.close?.icons ?? 'close'"
                  />
                </template>
              </fe-button>

              <fe-button
                v-if="modal.confirmActionButton"
                :color="modal.customButtons?.confirm?.color ?? 'border'"
                :fill="modal.customButtons?.confirm?.fill ?? 'outline'"
                class="confirm-btn with_border"
                data-test="modal__confirm__button"
                @click="handleConfirmClick"
              >
                <span>{{
                  modal.customButtons?.confirm?.label
                    ? $t(modal.customButtons?.confirm?.label)
                    : modal.confirmLabel
                }}</span>
                <template #icon-right>
                  <fe-icon
                    :icon="
                      modal.customButtons?.confirm?.icons ?? 'chevron-right'
                    "
                  />
                </template>
              </fe-button>
            </footer>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">
@import '@/styles/_constants.scss';
@import '@/styles/_mixins.scss';

.fe-modal {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 1001;
  .overlay {
    @include z-index('modal');
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: rgba($color-base, 0.3);
    display: flex;
    justify-content: center;
    align-items: center;

    z-index: 39;
    position: absolute;
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center;
  }

  .modal {
    transition: all 0.3s ease-in-out;
    display: grid;
    grid-template-rows: minmax(min-content, max-content) 1fr;
    box-shadow: -1.6rem 1.8rem 2.4rem rgba(var(--color-black) / 0.05);

    &.delivery-modal {
      width: 82.3rem;
    }
    &.picking-modal {
      min-height: 28.5rem;
    }
    &.logout-modal {
      .body {
        height: 7rem;
        padding-top: 2rem;
        .align-left {
          font-weight: 600;
          font-size: 1rem;
          text-align: center;
        }
      }
      @include tablet-small-landscape-up {
        width: 33rem;
      }
    }
    &.--no-footer {
      .body {
        border-radius: 0 0 0.8rem 0.8rem;
      }
    }
    &.modal-warning {
      .header {
        background: $color-alert-main;
      }
    }

    &.prints-errors,
    &.modal-error {
      .header {
        background: rgb(var(--color-error));
      }
    }
    &.modal-black {
      .header {
        background: rgb(var(--color-black));
      }
    }
    &.modal-stepper {
      width: auto;
    }

    .body {
      padding-top: 1rem;
    }

    &.returns-payments {
      // custom class for fe-returns-payments
      width: 87rem;

      .body p {
        text-align: center;
        color: rgb(var(--color-grey-500));
      }
    }
    &.user_modal {
      justify-content: center;
      width: 36.2rem;
    }
    &.abs-crm-modal {
      width: 38rem;
      p {
        font-size: var(--font-size-sm);
      }
      .footer {
        padding: 0.5rem 3rem 1.8rem 3rem;
      }
      .confirm-btn {
        --background: rgb(var(--color-info));
        border: none;
      }
    }

    &.input-modal.modal-info {
      width: min(42rem, 92vw);
      max-width: 42rem;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 0.8rem 2.4rem rgba(var(--color-black) / 0.12);

      @include tablet-small-landscape-up {
        width: min(42rem, 92vw);
      }

      .header {
        border-radius: 0;
        min-height: 3.75rem;
        padding: 0 1.25rem 0 1.5rem;

        .header__title {
          font-weight: 700;
          font-size: 1rem;
          letter-spacing: 0.02em;
        }
      }

      .body {
        background: rgb(var(--color-white));
        padding: 1.5rem 1.5rem 0;
        border-radius: 0;

        > p {
          margin: 0 0 1.25rem;
          line-height: 1.5;
          color: rgb(var(--color-black));
          font-size: var(--font-size-md);

          :deep(b),
          :deep(strong) {
            font-weight: 700;
            color: rgb(var(--color-black));
          }
        }
      }
    }

    @include tablet-small-landscape-up {
      width: 53rem;
    }
  }

  .header {
    background: rgb(var(--color-info));
    color: rgb(var(--color-white));
    padding: 0 2rem;
    box-sizing: border-box;
    grid-row: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 0.8rem 0.8rem 0 0;
    min-height: 4rem;

    &__title {
      text-transform: uppercase;
      margin: 0;
      font-weight: 600;
      font-size: var(--font-size-md);
    }

    &__icon ion-icon {
      fill: white !important;
    }
  }

  .body {
    grid-row: 2;
    padding: 0 2rem;
    background: rgb(var(--color-grey-100));

    p.align-center {
      text-align: center;
    }
  }

  .footer {
    display: flex;
    justify-content: space-between;
    border-radius: 0 0 0.8rem 0.8rem;
    padding: 0.5rem 2rem 1.8rem 2rem;
    background: rgb(var(--color-grey-100));

    &.align-center {
      justify-content: center;
    }

    & > .close-btn {
      margin-right: 0.5rem;
    }

    & > * {
      --border-style: none;
    }
  }

  .with_border {
    border: 1px solid var(--border-color);
    &.info {
      border: none;
    }
  }
  .alipay-modal {
    .body {
      border-bottom-left-radius: 0.8rem;
      border-bottom-right-radius: 0.8rem;
    }
  }
}
</style>
